import React, { useState, useRef, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Intent, Tag } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import { renderMentionSuggestion } from './mention-suggestions';
import { SimpleFileUpload } from './SimpleFileUpload';
import { AttachmentPreview } from './AttachmentPreview';
import { getCurrentStytchJWT } from '../../services/api';
import './TiptapMessageComposer.css';

interface TiptapMessageComposerProps {
  conversationId: string;
  onMessageSent?: (message: string) => void;
  disabled?: boolean;
}

/**
 * Extract mentions from Tiptap JSON structure
 */
function extractMentions(tiptapJson: any): Array<{
  type: string;
  id: string;
  label: string;
  metadata?: any;
}> {
  const mentions: any[] = [];
  
  function traverse(node: any) {
    if (node.type === 'mention' && node.attrs) {
      mentions.push({
        type: node.attrs.type || 'unknown',
        id: node.attrs.id,
        label: node.attrs.label,
        metadata: node.attrs.metadata,
      });
    }
    
    if (node.content) {
      node.content.forEach(traverse);
    }
  }
  
  if (tiptapJson?.content) {
    tiptapJson.content.forEach(traverse);
  }
  
  return mentions;
}

const TiptapMessageComposer: React.FC<TiptapMessageComposerProps> = observer(({
  conversationId,
  onMessageSent,
  disabled = false,
}) => {
  const appStore = useAppStore();
  const { chat: chatStore } = appStore;
  const [isStreaming, setIsStreaming] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [currentMentions, setCurrentMentions] = useState<any[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Initialize Tiptap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: {},
        codeBlock: {},
        bulletList: {},
        orderedList: {},
      }),
      
      Placeholder.configure({
        placeholder: 'Type a message... Use @ to mention mindmaps/nodes',
      }),
      
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        renderLabel({ node }) {
          return `@${node.attrs.label}`;
        },
        suggestion: renderMentionSuggestion(),
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
      },
    },
    onUpdate: ({ editor }) => {
      // Track mentions in real-time
      const json = editor.getJSON();
      const mentions = extractMentions(json);
      setCurrentMentions(mentions);
    },
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  const handleSend = async () => {
    if (!editor || isStreaming || disabled) return;

    const plainText = editor.getText().trim();
    if (!plainText && attachments.length === 0) return;

    if (!conversationId) {
      console.error('No conversation ID provided');
      chatStore.setError('Please select or create a conversation first');
      return;
    }

    // Extract message data
    const htmlContent = editor.getHTML();
    const tiptapJson = editor.getJSON();
    const mentions = extractMentions(tiptapJson);

    console.log('[TiptapMessageComposer] Sending message with:', {
      plainText: plainText.substring(0, 100),
      mentions: mentions.length,
      attachments: attachments.length,
    });

    // Capture attachments before clearing
    const messageAttachments = [...attachments];
    const messageMentions = [...mentions];

    // Clear editor and UI state
    editor.commands.clearContent();
    setAttachments([]);
    setCurrentMentions([]);
    onMessageSent?.(plainText);

    setIsStreaming(true);
    
    // Create abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Send message with mentions and attachments (pass full attachment data for optimistic update)
      await chatStore.sendMessageWithMentions(
        conversationId,
        plainText,
        htmlContent,
        tiptapJson,
        messageMentions,
        messageAttachments.map(a => a.id), // Send attachment IDs to backend
        messageAttachments, // Pass full attachment metadata for optimistic update
        abortController.signal // Pass abort signal to cancel streaming
      );
      
      // Auto-generate title if this is the first message
      if (chatStore.messages.length === 2 && 
          chatStore.activeConversation?.title === 'New Conversation') {
        chatStore.generateConversationTitle(conversationId).catch(err => {
          console.error('Failed to generate title:', err);
        });
      }
      
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Message sending aborted');
      } else {
        console.error('Error sending message:', error);
        chatStore.setError(error.message || 'Failed to send message');
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
    }
  };

  // File upload handlers
  const handleFileUpload = useCallback(async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) {
      console.log('No files provided');
      return;
    }
    
    const fileArray = Array.from(files);
    console.log(`Starting upload of ${fileArray.length} file(s)`);
    
    // Check model capabilities - only validate if capabilities are loaded
    const capabilities = chatStore.selectedModelCapabilities;
    console.log('Model capabilities for validation:', capabilities);
    
    // If capabilities aren't loaded yet (user not logged in or models not loaded),
    // allow uploads to proceed - backend will handle validation
    const invalidFiles: string[] = [];
    const validFiles: File[] = [];
    
    if (capabilities) {
      // Capabilities are loaded - validate files
      const supportsVision = capabilities.supportsVision;
      const supportsFiles = capabilities.supportsFiles;
      const supportedMimeTypes = capabilities.supportedMimeTypes || [];
      const maxFileSize = capabilities.maxFileSize || 20 * 1024 * 1024; // 20MB default
      
      console.log('Validating against capabilities:', {
        supportsVision,
        supportsFiles,
        supportedMimeTypes,
        maxFileSize: `${(maxFileSize / (1024 * 1024)).toFixed(1)}MB`
      });
      
      for (const file of fileArray) {
        const isImage = file.type.startsWith('image/');
        
        // Check if model supports this type of file
        if (isImage && !supportsVision) {
          invalidFiles.push(`${file.name}: This model doesn't support images`);
          continue;
        }
        
        if (!isImage && !supportsFiles) {
          invalidFiles.push(`${file.name}: This model only supports images`);
          continue;
        }
        
        // Check MIME type if supported types are specified
        if (supportedMimeTypes.length > 0 && !supportedMimeTypes.includes(file.type)) {
          invalidFiles.push(`${file.name}: File type ${file.type} not supported. Supported: ${supportedMimeTypes.join(', ')}`);
          continue;
        }
        
        // Check file size
        if (file.size > maxFileSize) {
          const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(1);
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
          invalidFiles.push(`${file.name}: File too large (${fileSizeMB}MB > ${maxSizeMB}MB limit)`);
          continue;
        }
        
        validFiles.push(file);
      }
      
      // Show warnings for invalid files
      if (invalidFiles.length > 0) {
        const message = `Some files cannot be uploaded:\n${invalidFiles.join('\n')}`;
        chatStore.setError(message);
        console.warn('Invalid files:', invalidFiles);
      }
      
      // If no valid files, return early
      if (validFiles.length === 0) {
        console.log('No valid files to upload after validation');
        return;
      }
    } else {
      // No capabilities loaded - allow all files through (backend will validate)
      console.log('No capabilities loaded - skipping validation, uploading all files');
      validFiles.push(...fileArray);
    }
    
    setIsUploading(true);
    
    try {
      // Get auth token for upload
      const jwt = getCurrentStytchJWT();
      if (!jwt) {
        throw new Error('Not authenticated - please log in');
      }
      
      const uploadedFiles: any[] = [];
      const baseUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:4011';
      
      // Upload each valid file to backend
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        console.log(`Uploading file ${i + 1}/${validFiles.length}: ${file.name} (${file.size} bytes)`);
        
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${baseUrl}/users/upload/file`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${jwt}`,
          },
          body: formData,
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log('Upload successful:', result);
          
          uploadedFiles.push({
            id: result.fileUrl, // Use the fileUrl as ID for now
            fileName: result.fileName || file.name,
            fileSize: result.fileSize || file.size,
            mimeType: result.mimeType || file.type,
            url: result.fileUrl,
            isImage: file.type.startsWith('image/'),
          });
        } else {
          const errorText = await response.text();
          console.error(`Upload failed for ${file.name}: ${response.status} ${response.statusText}`, errorText);
          chatStore.setError(`Failed to upload ${file.name}: ${response.statusText}`);
        }
      }
      
      if (uploadedFiles.length > 0) {
        console.log(`Successfully uploaded ${uploadedFiles.length} file(s):`, uploadedFiles);
        setAttachments(prev => {
          const newAttachments = [...prev, ...uploadedFiles];
          console.log('Updated attachments state:', newAttachments);
          return newAttachments;
        });
      }
      
    } catch (error) {
      console.error('File upload failed:', error);
      chatStore.setError('File upload failed: ' + (error as Error).message);
    } finally {
      setIsUploading(false);
    }
  }, [chatStore]);

  // Handle paste events for images/files
  const handlePaste = useCallback((e: ClipboardEvent) => {
    console.log('Paste event detected');
    
    const items = e.clipboardData?.items;
    if (!items) return;
    
    const files: File[] = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          console.log('Pasted file detected:', file.name, file.type);
          files.push(file);
        }
      }
    }
    
    if (files.length > 0) {
      handleFileUpload(files);
      e.preventDefault();
    }
  }, [handleFileUpload]);

  // Set up paste event listener
  useEffect(() => {
    if (!editor) return;
    
    const editorElement = editor.view.dom;
    if (!editorElement) return;
    
    const pasteHandler = (e: ClipboardEvent) => {
      handlePaste(e);
    };
    
    editorElement.addEventListener('paste', pasteHandler);
    
    return () => {
      editorElement.removeEventListener('paste', pasteHandler);
    };
  }, [editor, handlePaste]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  }, [handleFileUpload]);

  if (!editor) {
    return <div className="message-composer">Loading editor...</div>;
  }

  return (
    <div className="message-composer tiptap-composer">
      <div 
        className={`tiptap-container ${isDragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Capabilities bar */}
        <div className="capabilities-bar">
          <div className="capabilities-tags">
            <span className="capability-tag supported">
              🗺️ Mindmaps
            </span>
            <span className="capability-tag supported">
              📍 Nodes
            </span>
          </div>
          
          <div className="shortcuts">
            <span><kbd>@</kbd> mention</span>
            <span><kbd>↵</kbd> send</span>
            <span><kbd>⇧↵</kbd> new line</span>
          </div>
        </div>

        {/* Drag overlay */}
        {isDragging && (
          <div className="drag-overlay">
            <div className="drag-content">
              <div className="drag-icon">📎</div>
              <div className="drag-text">Drop files here</div>
            </div>
          </div>
        )}

        {/* Editor */}
        <div className="editor-wrapper">
          <EditorContent 
            editor={editor}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          
          {isUploading && (
            <div className="uploading-overlay">
              <div>Uploading...</div>
            </div>
          )}
        </div>

        {/* Attachments and Mentions preview */}
        {(attachments.length > 0 || currentMentions.length > 0) && (
          <div className="preview-bar">
            <div className="preview-items">
              {attachments.length > 0 && (
                <>
                  <span className="preview-label">
                    {attachments.length} attachment{attachments.length !== 1 ? 's' : ''}:
                  </span>
                  {attachments.map((att, idx) => {
                    console.log(`Rendering attachment ${idx}:`, att);
                    return (
                      <AttachmentPreview
                        key={att.id || idx}
                        attachment={att}
                        onRemove={() => {
                          console.log('Removing attachment:', att.id);
                          setAttachments(prev => prev.filter((_, i) => i !== idx));
                        }}
                        showRemove={true}
                      />
                    );
                  })}
                </>
              )}
              
              {currentMentions.length > 0 && (
                <>
                  {attachments.length > 0 && (
                    <span className="separator">|</span>
                  )}
                  <span className="preview-label">
                    {currentMentions.length} mention{currentMentions.length !== 1 ? 's' : ''}:
                  </span>
                  {currentMentions.map((mention, idx) => (
                    <Tag
                      key={idx}
                      icon={IconNames.AT}
                      intent={Intent.PRIMARY}
                      minimal={true}
                      round={true}
                    >
                      {mention.label}
                    </Tag>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {/* Action bar */}
        <div className="action-bar">
          <div className="upload-buttons">
            <SimpleFileUpload
              onFileSelect={(files) => handleFileUpload(files)}
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
              multiple={true}
              disabled={disabled || isUploading}
              buttonText="Image"
              icon={IconNames.MEDIA}
              intent={Intent.SUCCESS}
              minimal={true}
            />
            
            <SimpleFileUpload
              onFileSelect={(files) => handleFileUpload(files)}
              accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              multiple={true}
              disabled={disabled || isUploading}
              buttonText="File"
              icon={IconNames.PAPERCLIP}
              minimal={true}
            />
            
            <span className="upload-hint">
              paste/drag files supported
            </span>
          </div>
          
          {isStreaming ? (
            <Button
              icon={IconNames.STOP}
              intent="danger"
              onClick={handleStop}
              text="Stop"
            />
          ) : (
            <Button
              icon={IconNames.SEND_MESSAGE}
              intent="primary"
              onClick={handleSend}
              disabled={disabled || isUploading || (!editor.getText().trim() && attachments.length === 0)}
              text="Send"
            />
          )}
        </div>
      </div>
    </div>
  );
});

export default TiptapMessageComposer;
